package com.solevo.dist.pages.quotes

import androidx.compose.runtime.*
import com.solevo.dist.components.CenteredLoader
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.viewmodels.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.components.StatusPill
import com.solevo.dist.theme.styles.tableHeaderCell
import com.solevo.dist.theme.styles.tableBodyCell
import com.solevo.dist.theme.styles.tableBodyCellBold
import com.solevo.dist.theme.styles.tableRow
import com.solevo.dist.theme.styles.tableHeaderRow
import com.solevo.dist.theme.styles.sectionTitle
import org.jetbrains.compose.web.css.*
import com.solevo.dist.components.dialogs.DeleteConfirmDialog
import com.solevo.dist.components.dialogs.ErrorDialog

@Page("/quotes")
@Composable
fun QuotesScreen() {
    RequireAuth { QuotesPage() }
}

@Composable
fun QuotesPage() {
    WaitForAuthAndViewModel(
        key = "quotes",
        viewModelFactory = { QuotesViewModel() },
        onViewModelReady = { vm -> vm.start() }
    ) { vm ->
        DisposableEffect(Unit) { onDispose { vm.stop() } }
        val ui by vm.state.collectAsState()
        var deleteTargetId by remember { mutableStateOf<String?>(null) }

        DashboardPageLayout(header = {
            QuotesHeader(
                ui = ui,
                onTabSelected = { t -> vm.selectTab(t) }
            )
        }) {
            QuotesBody(
                ui = ui,
                onRequestDelete = { id -> deleteTargetId = id },
                onClearError = { vm.clearError() }
            )
            if (deleteTargetId != null) {
                DeleteConfirmDialog(
                    onConfirm = {
                        val id = deleteTargetId
                        // Close dialog immediately; perform delete in background via HTTP cancel
                        deleteTargetId = null
                        if (id != null) {
                            vm.cancelQuoteViaHttp(id)
                        }
                    },
                    onCancel = { deleteTargetId = null }
                )
            }
        }
    }
}

@Composable
private fun VSpace(h: CSSLengthValue) = Box(Modifier.height(h))

@Composable
private fun QuotesHeader(ui: QuotesUiState, onTabSelected: (QuotesTab) -> Unit) {
    Column(Modifier.fillMaxWidth().padding(24.px)) {
        val strings = LocalStrings.current
        SpanText(strings.quotesTitle, Modifier.sectionTitle())
        VSpace(20.px)

        Box(
            Modifier
                .fillMaxWidth()
                .position(Position.Relative)
        ) {
            Box(
                Modifier
                    .position(Position.Absolute)
                    .left(0.px)
                    .right(0.px)
                    .bottom(1.px)
                    .height(1.px)
                    .backgroundColor(rgb(230, 232, 235))
                    .zIndex(0)
            )

            Row(
                Modifier
                    .gap(24.px)
                    .align(Alignment.BottomStart)
                    .zIndex(1)
            ) {
                QuotesTab.values().forEach { t ->
                    QuotesTopTab(
                        label = when (t) {
                            QuotesTab.All -> strings.quotesTabAll
                            QuotesTab.Awaiting -> strings.quotesTabAwaiting
                            QuotesTab.Received -> strings.quotesTabReceived
                        },
                        selected = ui.selectedTab == t,
                        onClick = { onTabSelected(t) }
                    )
                }
            }
        }
    }
}

@Composable
private fun QuotesBody(ui: QuotesUiState, onRequestDelete: (String) -> Unit, onClearError: () -> Unit) {
    Box(Modifier.fillMaxWidth().padding(24.px)) {
        when {
            ui.loading -> CenteredLoader()
            else -> QuotesTable(ui.rows, onRequestDelete)
        }
        ui.error?.let { msg ->
            ErrorDialog(message = msg) { onClearError() }
        }
    }
}

// Removed inline error box in favor of modal ErrorDialog

@Composable
private fun QuotesTable(quotes: List<QuoteRowUi>, onRequestDelete: (String) -> Unit) {
    Column(
        Modifier.fillMaxWidth()
    ) {
        val strings = LocalStrings.current
        GridRow(
            modifier = Modifier.tableHeaderRow(),
            amount = strings.quotesHeaderAmount,
            status = " ",
            description = strings.quotesHeaderDescription,
            pickup = strings.quotesHeaderPickupDate,
            created = strings.quotesHeaderDateCreated,
            showDelete = false,
            isHeader = true
        )

        if (quotes.isEmpty()) {
            com.solevo.dist.components.EmptyState(
                title = strings.quotesEmpty,
                message = strings.quotesEmptyMessage
            )
            return@Column
        }

        val context = com.varabyte.kobweb.core.rememberPageContext()
        quotes.forEach { q ->
            GridRow(
                modifier = Modifier,
                amount = q.amountText,
                status = q.statusText,
                description = q.description,
                pickup = q.pickupDateText,
                created = q.createdDateText,
                pillHex = q.statusColorHex,
                showDelete = false, // Hidden for now, but kept for future use
                isHeader = false,
                onDelete = { onRequestDelete(q.id) },
                onClick = { context.router.navigateTo(com.solevo.dist.navigation.Screen.QuoteDetailsPage.withId(q.id)) }
            )
        }
    }
}

@Composable
private fun GridRow(
    modifier: Modifier = Modifier,
    amount: String,
    status: String,
    description: String,
    pickup: String,
    created: String,
    pillHex: String? = null,
    showDelete: Boolean,
    isHeader: Boolean,
    onClick: (() -> Unit)? = null,
    onDelete: (() -> Unit)? = null
) {
    Row(
        modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .styleModifier { property("grid-template-columns", if (isHeader) "0.6fr 0.6fr 1fr 1fr 1fr 40px" else "0.6fr 0.6fr 1fr 1fr 1fr 40px") }
            .then(if (isHeader) Modifier.tableHeaderRow() else Modifier.tableRow())
    ) {
        if (isHeader) {
            SpanText(amount, Modifier.tableHeaderCell())
        } else {
            val cellModifier = Modifier.tableBodyCellBold().then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            SpanText(amount, cellModifier)
        }
        if (isHeader) {
            SpanText(status, Modifier.tableHeaderCell())
        } else {
            Row(Modifier.cursor(Cursor.Pointer).onClick { onClick?.invoke() }) { StatusPill(status, pillHex ?: "#888888", useIconForInvoiceStatuses = false) }
        }
        if (isHeader) {
            SpanText(description, Modifier.tableHeaderCell())
        } else {
            val cellModifier = Modifier.tableBodyCell().then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            SpanText(description, cellModifier)
        }
        if (isHeader) {
            SpanText(pickup, Modifier.tableHeaderCell())
        } else {
            val cellModifier = Modifier.tableBodyCell().then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            SpanText(pickup, cellModifier)
        }
        if (isHeader) {
            SpanText(created, Modifier.tableHeaderCell())
        } else {
            val cellModifier = Modifier.tableBodyCell().then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            SpanText(created, cellModifier)
        }
        if (!isHeader) DeleteCell(visible = showDelete) { onDelete?.invoke() }
    }
}



@Composable
private fun DeleteCell(visible: Boolean, onClick: (() -> Unit)? = null) {
    if (!visible) {
        Box(Modifier.width(24.px))
        return
    }
    Row(Modifier.justifyContent(JustifyContent.End)) {
        com.varabyte.kobweb.silk.components.graphics.Image(
            src = com.solevo.dist.util.Res.Icon.delete,
            alt = "delete",
            modifier = Modifier
                .width(18.px)
                .height(18.px)
                .cursor(Cursor.Pointer)
                .onClick {
                    // Stop row navigation by stopping event propagation
                    onClick?.invoke()
                }
        )
    }
}

@Composable
private fun QuotesTopTab(
    label: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    Box(
        Modifier
            .cursor(Cursor.Pointer)
            .padding(leftRight = 12.px, topBottom = 10.px)
            .styleModifier {
                if (selected) {
                    property("box-shadow", "inset 0 -3px 0 0 black")
                }
            }
            .onClick { onClick() }
    ) {
        SpanText(
            label,
            Modifier
                .fontSize(15.px)
                .fontWeight(if (selected) FontWeight.Bold else FontWeight.Normal)
                .color(rgb(36, 41, 47))
        )
    }
}


